import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import TrainerLayout from './TrainerLayout';
import SearchBar from '../../components/commons/searchbar/SearchBar';
import TrainerAddDogModal from './TrainerAddDogModal';
import { useToast } from '../commons/toast/ToastContext';
import '../../styles/Podopieczni.css';

const Podopieczni = () => {
  const { t } = useTranslation(['dogs', 'common']);
  const { trainer } = useContext(TrainerContext);
  const navigate = useNavigate();

  const [dogs, setDogs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showAddDogModal, setShowAddDogModal] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const toast = useToast();

  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [processingRequestId, setProcessingRequestId] = useState(null);

  const fetchPendingRequests = async () => {
      if (!trainer || !trainer.schoolId) return;
      try {
          const response = await axios.get(`/api/v1/dog/school-requests/${trainer.schoolId}`);
          setPendingRequests(response.data);
          setPendingCount(response.data.length);
      } catch (err) {
          console.error('Error fetching pending requests:', err);
      }
  };

  useEffect(() => {
      fetchPendingRequests();
  }, [trainer]);

  const handleApproveRequest = async (requestId) => {
      setProcessingRequestId(requestId);
      try {
          await axios.post(`/api/v1/dog/school-requests/${requestId}/approve`);
          toast.success(t('dogs:pupilApproved'));
          fetchPendingRequests();
          setRefreshCounter(prev => prev + 1);
      } catch (error) {
          if (error.response?.data?.message) {
              toast.error(error.response.data.message);
          } else {
              toast.error(t('dogs:pupilApproveError'));
          }
      } finally {
          setProcessingRequestId(null);
      }
  };

  const handleRejectRequest = async (requestId) => {
      setProcessingRequestId(requestId);
      try {
          await axios.post(`/api/v1/dog/school-requests/${requestId}/reject`);
          toast.success(t('dogs:pupilRequestRejected'));
          fetchPendingRequests();
      } catch (error) {
          toast.error(t('dogs:pupilRejectError'));
      } finally {
          setProcessingRequestId(null);
      }
  };

  useEffect(() => {
    if (!trainer || !trainer.schoolId) return;

    const fetchDogs = async () => {
      try {
        setLoading(true);
        let endpoint = `/api/v1/dog/${trainer.schoolId}?page=${currentPage}&size=${pageSize}`;

        if (searchTerm.trim()) {
          endpoint += `&search=${encodeURIComponent(searchTerm.trim())}`;
          setIsSearching(true);
        } else {
          setIsSearching(false);
        }

        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
        });

        setDogs(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
      } catch (err) {
        setError(t('dogs:fetchDogsError'));
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchDogs();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [trainer, currentPage, pageSize, searchTerm, refreshCounter]);

  const handleDogAdded = () => {
    setShowAddDogModal(false);
    setCurrentPage(0);
    setRefreshCounter(prev => prev + 1);
    toast.success(t('dogs:pupilAddedSuccess'));
  };

  return (
      <TrainerLayout pageTitle={t('dogs:yourPupils')}>
        <div className="podopieczni-page-container">
          <h1 className="podopieczni-mobile-title">{t('dogs:pupils')}</h1>

          <div className="podopieczni-search-section">
            <SearchBar
                width="400px"
                value={searchTerm}
                onChange={setSearchTerm}
                onSearch={() => setCurrentPage(0)}
                placeholder={t('dogs:searchPlaceholder')}
            />

            <div className="podopieczni-right-controls">
              <button
                  className="podopieczni-add-dog-btn"
                  onClick={() => setShowAddDogModal(true)}
              >
                {t('dogs:addPupil')}
              </button>

            <div className="podopieczni-pagination-controls-inline">
              <label className="podopieczni-page-size-label">{t('dogs:perPage')}:</label>
              <select
                  className="podopieczni-page-size-select"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(0);
                  }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>
            </div>
          </div>

          {/* Pending dog requests */}
          {pendingRequests.length > 0 && (
              <div className="podopieczni-pending-section">
                  <h3 className="podopieczni-pending-title">
                      {t('dogs:pendingRequests', { count: pendingCount })}
                  </h3>
                  <div className="podopieczni-pending-list">
                      {pendingRequests.map((request) => (
                          <div key={request.id} className="podopieczni-pending-card">
                              <div className="podopieczni-pending-info">
                                  <span className="podopieczni-pending-dog-name">{request.dogName}</span>
                                  <span className="podopieczni-pending-breed">{request.dogBreed}</span>
                                  <span className="podopieczni-pending-owner">{t('dogs:caretaker')}: {request.ownerName}</span>
                              </div>
                              <div className="podopieczni-pending-actions">
                                  <button
                                      className="podopieczni-pending-approve-btn"
                                      onClick={() => handleApproveRequest(request.id)}
                                      disabled={processingRequestId === request.id}
                                  >
                                      {processingRequestId === request.id ? "..." : t('dogs:approve')}
                                  </button>
                                  <button
                                      className="podopieczni-pending-reject-btn"
                                      onClick={() => handleRejectRequest(request.id)}
                                      disabled={processingRequestId === request.id}
                                  >
                                      {t('dogs:reject')}
                                  </button>
                              </div>
                          </div>
                      ))}
                  </div>
              </div>
          )}

          {loading ? (
              <p className="podopieczni-loading-text">{t('common:loading')}</p>
          ) : error ? (
              <p className="podopieczni-error-message">{error}</p>
          ) : (
              <>
                {isSearching && dogs.length > 0 && (
                    <div className="podopieczni-search-results-info">
                      {t('dogs:searchFound')}: {dogs.length} {dogs.length === 1 ? t('dogs:result') :
                        dogs.length < 5 ? t('dogs:resultsFew') : t('dogs:resultsMany')}
                    </div>
                )}

                <div className="podopieczni-table-section">
                  <table className="podopieczni-dogs-table">
                    <thead>
                    <tr>
                      <th>{t('dogs:name')}</th>
                      <th>{t('dogs:breed')}</th>
                      <th>{t('dogs:age')}</th>
                      <th>{t('dogs:gender')}</th>
                      <th>{t('dogs:ownerName')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {dogs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="podopieczni-no-data-row">
                            {isSearching
                                ? t('dogs:noSearchResults')
                                : t('dogs:noPupils')}
                          </td>
                        </tr>
                    ) : (
                        dogs.map((dog) => (
                            <tr
                                key={dog.id}
                                onClick={() => navigate(`/dog/details/${dog.id}`)}
                                className="podopieczni-clickable-row"
                            >
                              <td className="podopieczni-centered-column"><strong>{dog.name}</strong></td>
                              <td className="podopieczni-centered-column">{dog.breed}</td>
                              <td className="podopieczni-centered-column">{dog.age}</td>
                              <td className={`podopieczni-gender-text ${dog.gender === "FEMALE" ? "podopieczni-gender-samica" : "podopieczni-gender-samiec"}`}>
                                {dog.gender === "FEMALE" ? t('dogs:female') : t('dogs:male')}
                              </td>
                              <td className="podopieczni-centered-column">
                                {dog.ownerName || <span className="podopieczni-no-owner-badge">{t('dogs:noOwner')}</span>}
                              </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                  </table>
                </div>

                <div className="podopieczni-mobile-cards">
                  {dogs.length === 0 ? (
                      <div className="podopieczni-mobile-no-data">
                        {isSearching ? t('dogs:noSearchResults') : t('dogs:noPupils')}
                      </div>
                  ) : (
                      dogs.map((dog) => (
                          <div
                              key={dog.id}
                              className="podopieczni-dog-card"
                              onClick={() => navigate(`/dog/details/${dog.id}`)}
                          >
                            <div className="podopieczni-dog-card-header">
                              <div className="podopieczni-dog-name-section">
                                <h3 className="podopieczni-dog-name">{dog.name}</h3>
                                <p className="podopieczni-dog-breed">{dog.breed}</p>
                              </div>
                              <span className={`podopieczni-dog-gender-mobile ${dog.gender === "FEMALE" ? "podopieczni-gender-samica" : "podopieczni-gender-samiec"}`}>
                                                {dog.gender === "FEMALE" ? t('dogs:female') : t('dogs:male')}
                                            </span>
                            </div>

                            <div className="podopieczni-dog-details-divider"></div>

                            <div className="podopieczni-dog-details">
                              <div className="podopieczni-dog-detail-item">
                                <span className="podopieczni-dog-detail-label">{t('dogs:age')}</span>
                                <span className="podopieczni-dog-detail-value">{dog.age}</span>
                              </div>
                              <div className="podopieczni-dog-detail-item">
                                <span className="podopieczni-dog-detail-label">{t('dogs:caretaker')}</span>
                                <span className="podopieczni-dog-detail-value">
                                  {dog.ownerName || <span className="podopieczni-no-owner-badge">{t('dogs:noOwner')}</span>}
                                </span>
                              </div>
                            </div>
                          </div>
                      ))
                  )}
                </div>

                <div className="podopieczni-pagination-section">
                  <button
                      className="podopieczni-pagination-btn"
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    {t('common:previous')}
                  </button>
                  <span className="podopieczni-page-info">
                                {t('dogs:pageInfo', { current: currentPage + 1, total: totalPages })}
                            </span>
                  <button
                      className="podopieczni-pagination-btn"
                      disabled={currentPage + 1 >= totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    {t('common:next')}
                  </button>
                </div>
              </>
          )}
        </div>

        <TrainerAddDogModal
            isOpen={showAddDogModal}
            onClose={() => setShowAddDogModal(false)}
            onDogAdded={handleDogAdded}
        />
      </TrainerLayout>
  );
};

export default Podopieczni;
